import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokens, Stack, Icon } from '@/design';
import { CostConfirmDialog } from '@/components/notifications/CostConfirmDialog';
import { useViewerStore } from '@/state/useViewerStore';

/**
 * CostConfirmRoute — WP-E9 cost confirmation route scaffold.
 * 
 * Dedicated route for cost confirmation workflow. Demonstrates the
 * confirmation dialog and provides testing interface.
 */
export function CostConfirmRoute() {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [callDescription, setCallDescription] = useState('Generate detailed site analysis');
  
  const mockCostBudgetState = useViewerStore((state) => state.mockCostBudgetState);
  const setMockCostBudgetState = useViewerStore((state) => state.setMockCostBudgetState);

  const handleOpenDialog = () => {
    // Ensure we have cost budget state for the dialog
    if (!mockCostBudgetState) {
      setMockCostBudgetState({
        used: 75,
        total: 100,
        window: 'session',
        resetTime: '6:00 PM'
      });
    }
    setIsDialogOpen(true);
  };

  const handleConfirm = () => {
    console.log('[Cost Confirm] Operation confirmed:', callDescription);
    setIsDialogOpen(false);
    // In real implementation, would invoke the expensive operation
  };

  const handleCancel = () => {
    console.log('[Cost Confirm] Operation cancelled');
    setIsDialogOpen(false);
  };

  return (
    <div
      data-gvw-id="GVW-cost-confirm"
      style={{
        maxWidth: tokens.layout.popoverLg * 1.67 + 'px',
        margin: '0 auto',
        padding: tokens.space[4],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <Stack direction="column" gap={4}>
        <header>
          <Stack direction="row" align="center" gap={2}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                minHeight: tokens.density.touchMin,
                width: tokens.density.touchMin,
                border: 'none',
                borderRadius: tokens.radius.md,
                background: 'transparent',
                color: tokens.color.ink.secondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="arrow_back" size={20} decorative />
            </button>
            <h1 style={{
              margin: 0,
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.medium,
              color: tokens.color.ink.primary,
            }}>
              Cost Confirmation
            </h1>
          </Stack>
          <p style={{
            margin: `${tokens.space[2]} 0 0`,
            marginLeft: tokens.density.touchMin + 'px', // Align with title after back button
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}>
            Test the cost confirmation dialog for expensive copilot operations.
          </p>
        </header>

        <div style={{
          padding: tokens.space[4],
          background: tokens.color.surface[1],
          borderRadius: tokens.radius.input,
          border: tokens.border.thin,
        }}>
          <Stack direction="column" gap={3}>
            <div>
              <label 
                htmlFor="call-description"
                style={{
                  display: 'block',
                  marginBottom: tokens.space[1],
                  fontSize: tokens.font.size.sm,
                  fontWeight: tokens.font.weight.medium,
                  color: tokens.color.ink.primary,
                }}
              >
                Operation Description
              </label>
              <input
                id="call-description"
                type="text"
                value={callDescription}
                onChange={(e) => setCallDescription(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: tokens.density.touchMin,
                  padding: `${tokens.space[2]} ${tokens.space[3]}`,
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.input,
                  background: tokens.color.surface[0],
                  color: tokens.color.ink.primary,
                  fontSize: tokens.font.size.sm,
                  fontFamily: tokens.font.family.ui,
                }}
                placeholder="Enter operation description..."
              />
            </div>

            <div style={{
              padding: tokens.space[3],
              background: tokens.color.surface[0],
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
            }}>
              <Stack direction="row" align="center" gap={2}>
                <Icon name="info" size={16} decorative />
                <div style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.secondary }}>
                  This dialog appears automatically when expensive operations are triggered and the cost budget is approaching limits.
                </div>
              </Stack>
            </div>

            <button
              type="button"
              onClick={handleOpenDialog}
              style={{
                minHeight: tokens.density.touchMin,
                border: 'none',
                borderRadius: tokens.radius.md,
                background: 'var(--md-primary)',
                color: 'var(--md-on-primary)',
                paddingInline: tokens.space[4],
                cursor: 'pointer',
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.medium,
                alignSelf: 'flex-start',
              }}
            >
              <Icon name="payments" size={16} decorative style={{ marginRight: tokens.space[1] }} />
              Trigger Cost Confirmation
            </button>
          </Stack>
        </div>

        {mockCostBudgetState && (
          <div style={{
            padding: tokens.space[3],
            background: tokens.color.surface[1],
            borderRadius: tokens.radius.input,
            border: tokens.border.thin,
          }}>
            <Stack direction="column" gap={2}>
              <h3 style={{
                margin: 0,
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.medium,
                color: tokens.color.ink.primary,
              }}>
                Current Budget State
              </h3>
              <div style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.secondary }}>
                Used: {mockCostBudgetState.used} of {mockCostBudgetState.total} runs ({Math.round((mockCostBudgetState.used / mockCostBudgetState.total) * 100)}%)
              </div>
              <div style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.secondary }}>
                Window: {mockCostBudgetState.window}
              </div>
            </Stack>
          </div>
        )}
      </Stack>

      <CostConfirmDialog
        isOpen={isDialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        callDescription={callDescription}
        costClass="expensive"
      />
    </div>
  );
}